import { apiClient, unwrap, unwrapPaginated } from './client';
import type {
  Account,
  Invoice,
  InvoiceStatus,
  InvoiceType,
  JournalEntry,
  JournalEntryStatus,
  JournalType,
} from '@/types';

/**
 * Réponse du backend : tous les montants en string (BigInt sérialisé).
 * Le frontend convertit en number via les helpers de currency.ts.
 */
export interface FinanceKpis {
  id?:                  string;
  filialeId?:           string;
  periode:              string;     // 'YYYY-MM'
  currency:             string;     // 'FCFA' | 'GNF'
  caCents:              string;
  chargesCents:         string;
  resultatBrutCents:    string;
  ebitdaCents:          string;
  tresorerieCents:      string;
  bfrCents:             string;
  margeBruteCents:      string;
  caFcfaCents:          string;
  chargesFcfaCents:     string;
  ebitdaFcfaCents:      string;
  tresorerieFcfaCents:  string;
  /** Vrai quand ce tableau de bord agrège plusieurs sociétés (holding). */
  consolide?:           boolean;
  /** Filiales entrant dans le total, quand `consolide` est vrai. */
  filialesConsolidees?: { filialeKey: string; name: string; caFcfaCents: string }[];
  /** CA cumulé de janvier à la période affichée, calculé par le serveur. */
  cumulAnnuelCaCents?:  string;
  employeesCount:       number;
  dsoDays:              number;
  metrics:              Record<string, number>;
  computedAt?:          string;
}

export interface FinanceKpisHistory {
  months: number;
  series: FinanceKpis[];
}

// ── JOURNAL ──────────────────────────────────────────────────────────────────

export interface JournalQuery {
  journalType?: JournalType;
  status?:      JournalEntryStatus;
  dateFrom?:    string;
  dateTo?:      string;
  page?:        number;
  limit?:       number;
}

export interface CreateJournalLine {
  /** Tiers (client / fournisseur) — obligatoire sur un compte 40x / 41x (lot 2, étape 2.4). */
  contactId?: string;
  accountId:   string;
  label:       string;
  debitCents:  number;
  creditCents: number;
  currency?:   string;
}

export interface CreateJournalEntryPayload {
  journalType: JournalType;
  reference:   string;
  entryDate:   string;
  description: string;
  lines:       CreateJournalLine[];
}

/** Relevé de compte (30/10/2026). Montants en centimes (chaînes : grands entiers). */
export interface ReleveCompte {
  type: 'client' | 'fournisseur'; du: string; au: string;
  tiers: { id: string; nom: string; interlocuteur: string | null; adresse: string | null; pays: string | null; rccm: string | null; ncc: string | null; email: string | null; telephone: string | null };
  ouvertureCents: string; totalDebitCents: string; totalCreditCents: string; soldeFinalCents: string; phraseSolde: string;
  lignes: { date: string; numeroPiece: string | null; nature: string; reference: string | null; libelle: string | null; debitCents: string; creditCents: string; soldeCents: string }[];
  /** Détail par facture (recouvrement) : TTC, imputé, reste dû, retard depuis l'échéance. */
  factures: {
    lignes: { date: string; reference: string; ttcCents: string; imputeCents: string; resteCents: string; echeance: string; joursRetard: number }[];
    totalTtcCents: string; totalImputeCents: string; totalResteCents: string;
  };
  brouillonsExclus: number;
}

export const financeApi = {
  /** KPI snapshot pour la filiale connectée (mois courant par défaut, ou ?periode=YYYY-MM) */
  getKpis: async (periode?: string): Promise<FinanceKpis> => {
    const res = await apiClient.get('/finance/kpis', { params: periode ? { periode } : undefined });
    return unwrap<FinanceKpis>(res);
  },

  /** Historique des N derniers mois pour le graphique (default 6) */
  getKpisHistory: async (months = 6): Promise<FinanceKpisHistory> => {
    const res = await apiClient.get('/finance/kpis/history', { params: { months } });
    return unwrap<FinanceKpisHistory>(res);
  },

  /** Plan comptable SYSCOHADA de la filiale (comptes actifs, triés par code) */
  getAccounts: async (): Promise<Account[]> => {
    const res = await apiClient.get('/finance/accounts');
    return unwrap<Account[]>(res);
  },

  /** Liste paginée des écritures comptables */
  getJournal: async (query: JournalQuery = {}) => {
    const res = await apiClient.get('/finance/journal', { params: query });
    return unwrapPaginated<JournalEntry>(res);
  },

  /** Détail d'une écriture avec lignes + comptes */
  getJournalEntry: async (id: string): Promise<JournalEntry> => {
    const res = await apiClient.get(`/finance/journal/${id}`);
    return unwrap<JournalEntry>(res);
  },

  /** Création d'une écriture (status=draft, validation SYSCOHADA côté serveur) */
  createJournalEntry: async (payload: CreateJournalEntryPayload): Promise<JournalEntry> => {
    const res = await apiClient.post('/finance/journal', payload);
    return unwrap<JournalEntry>(res);
  },

  /** Validation d'une écriture draft → posted */
  postJournalEntry: async (id: string): Promise<JournalEntry> => {
    const res = await apiClient.post(`/finance/journal/${id}/post`);
    return unwrap<JournalEntry>(res);
  },

  // ── GRAND LIVRE (lot 2, étape 2.3) ─────────────────────────

  contrePasser: async (id: string, payload: { motif: string; date?: string }): Promise<JournalEntry> => {
    const res = await apiClient.post(`/finance/journal/${id}/contre-passer`, payload);
    return unwrap<JournalEntry>(res);
  },

  getPeriodes: async (): Promise<ExerciceComptable[]> => {
    const res = await apiClient.get('/finance/periodes');
    return unwrap<ExerciceComptable[]>(res);
  },

  cloturerPeriode: async (id: string) => {
    const res = await apiClient.post(`/finance/periodes/${id}/cloturer`);
    return unwrap(res);
  },

  rouvrirPeriode: async (id: string, motif: string) => {
    const res = await apiClient.post(`/finance/periodes/${id}/rouvrir`, { motif });
    return unwrap(res);
  },

  // ── LETTRAGE (lot 2, étape 2.4) ────────────────────────────

  getLignesLettrage: async (compte: string, contactId?: string): Promise<LignesLettrage> => {
    const res = await apiClient.get('/finance/lettrage/lignes', { params: { compte, contactId } });
    return unwrap<LignesLettrage>(res);
  },

  lettrer: async (lineIds: string[]) => {
    const res = await apiClient.post('/finance/lettrages', { lineIds });
    return unwrap(res);
  },

  annulerLettrage: async (id: string, motif: string) => {
    const res = await apiClient.post(`/finance/lettrages/${id}/annuler`, { motif });
    return unwrap(res);
  },

  // ── ÉTATS COMPTABLES (lot 2, étape 2.8) ────────────────────

  getEtatBalance: async (du?: string, au?: string): Promise<EtatBalance> => {
    const res = await apiClient.get('/finance/etats/balance', { params: { du, au } });
    return unwrap<EtatBalance>(res);
  },

  getEtatGrandLivre: async (p: { du?: string; au?: string; compte?: string; classe?: string }): Promise<EtatGrandLivre> => {
    const res = await apiClient.get('/finance/etats/grand-livre', { params: p });
    return unwrap<EtatGrandLivre>(res);
  },

  getEtatBalanceAgee: async (type: 'clients' | 'fournisseurs', au?: string): Promise<EtatBalanceAgee> => {
    const res = await apiClient.get('/finance/etats/balance-agee', { params: { type, au } });
    return unwrap<EtatBalanceAgee>(res);
  },

  // ── FOURNISSEURS (lot 2, étape 2.5) ────────────────────────

  getFacturesFournisseurs: async (statut?: string): Promise<FactureFournisseur[]> => {
    const res = await apiClient.get('/finance/fournisseurs/factures', { params: { statut } });
    return unwrap<FactureFournisseur[]>(res);
  },

  getReceptionsAFacturer: async (): Promise<ReceptionAFacturer[]> => {
    const res = await apiClient.get('/finance/fournisseurs/receptions-a-facturer');
    return unwrap<ReceptionAFacturer[]>(res);
  },

  creerFactureFournisseur: async (payload: Record<string, unknown>) => {
    const res = await apiClient.post('/finance/fournisseurs/factures', payload);
    return unwrap(res);
  },

  payerFactureFournisseur: async (id: string, payload: { montantCents: number; mode: string; datePaiement?: string; reference?: string }) => {
    const res = await apiClient.post(`/finance/fournisseurs/factures/${id}/paiements`, payload);
    return unwrap(res);
  },

  // ── FACTURES ───────────────────────────────────────────────

  getInvoices: async (query: InvoiceQuery = {}) => {
    const res = await apiClient.get('/finance/invoices', { params: query });
    return unwrapPaginated<Invoice>(res);
  },

  getInvoice: async (id: string): Promise<Invoice> => {
    const res = await apiClient.get(`/finance/invoices/${id}`);
    return unwrap<Invoice>(res);
  },

  createInvoice: async (payload: CreateInvoicePayload): Promise<Invoice> => {
    const res = await apiClient.post('/finance/invoices', payload);
    return unwrap<Invoice>(res);
  },

  updateInvoice: async (id: string, payload: CreateInvoicePayload): Promise<Invoice> => {
    const res = await apiClient.patch(`/finance/invoices/${id}`, payload);
    return unwrap<Invoice>(res);
  },

  markInvoicePaid: async (id: string): Promise<Invoice> => {
    const res = await apiClient.post(`/finance/invoices/${id}/pay`);
    return unwrap<Invoice>(res);
  },

  /** Validation proforma → facture finale (génère nouvelle ref FACT-) */
  validateInvoice: async (id: string): Promise<Invoice> => {
    const res = await apiClient.post(`/finance/invoices/${id}/validate`);
    return unwrap<Invoice>(res);
  },

  /**
   * Annulation d'une proforma ou d'un brouillon (motif obligatoire ≥ 5 chars).
   * Ticket SIMGROUP-2026-001 (MANQUE-01). Refusé si la facture a déjà généré
   * une écriture comptable — utiliser un avoir dans ce cas.
   */
  cancelInvoice: async (id: string, motif: string): Promise<Invoice> => {
    const res = await apiClient.post(`/finance/invoices/${id}/cancel`, { motif });
    return unwrap<Invoice>(res);
  },

  /**
   * Ticket SIMGROUP-2026-003 Sprint 2 — Télécharge le PDF OHADA de la facture.
   * Backend refuse 400 avec MISSING_LEGAL_EMITTER ou MISSING_LEGAL_CLIENT si
   * les mentions obligatoires ne sont pas renseignées. On propage l'erreur avec
   * son message métier pour que l'UI l'affiche via toast (M-01).
   *
   * Silent : opt-out du toast global (l'appelant gère lui-même l'erreur).
   */
  downloadInvoicePdf: async (id: string): Promise<{ blob: Blob; filename: string }> => {
    const res = await apiClient.get(`/finance/invoices/${id}/pdf`, {
      responseType: 'blob',
      _silentError: true,
    } as any);
    const blob: Blob = res.data;
    // Récupère le filename depuis Content-Disposition
    const cd = (res.headers['content-disposition'] || res.headers['Content-Disposition'] || '') as string;
    const match = cd.match(/filename="?([^";]+)"?/i);
    const filename = match?.[1] || `facture-${id}.pdf`;
    return { blob, filename };
  },

  // ── Relevés de compte clients / fournisseurs (30/10/2026) ──

  getReleve: async (contactId: string, du?: string, au?: string): Promise<ReleveCompte> =>
    unwrap<ReleveCompte>(await apiClient.get(`/finance/releves/${contactId}`, { params: { du, au } })),

  downloadRelevePdf: async (contactId: string, du?: string, au?: string): Promise<{ blob: Blob; filename: string }> => {
    const res = await apiClient.get(`/finance/releves/${contactId}/pdf`, { params: { du, au }, responseType: 'blob', _silentError: true } as any);
    const cd = (res.headers['content-disposition'] || '') as string;
    return { blob: res.data as Blob, filename: cd.match(/filename="?([^";]+)"?/i)?.[1] ?? 'releve.pdf' };
  },

  // ── Recouvrement — encaissements clients ───────────────────

  /**
   * Enregistre un encaissement, total ou partiel. Le backend refuse tout
   * montant supérieur au restant dû (REGLEMENT_SUPERIEUR_AU_RESTANT) plutôt
   * que de l'absorber : un trop-perçu se traite par avoir.
   */
  enregistrerReglement: async (id: string, payload: CreateReglementPayload): Promise<ReglementResult> => {
    const res = await apiClient.post(`/finance/invoices/${id}/reglements`, payload);
    return unwrap<ReglementResult>(res);
  },

  /** Historique des encaissements d'une facture, du plus récent au plus ancien. */
  getReglements: async (id: string): Promise<Reglement[]> => {
    const res = await apiClient.get(`/finance/invoices/${id}/reglements`);
    return unwrap<Reglement[]>(res);
  },

  // ── Sprint 4 — Module 10 (Balance âgée) ────────────────────
  getBalanceAgee: async (): Promise<BalanceAgee> => {
    const res = await apiClient.get('/finance/balance-agee');
    return unwrap<BalanceAgee>(res);
  },

  // ── LOT A — LA source unique des indicateurs financiers ────
  // Aucun écran ne réadditionne de montants de factures : il lit ceci.
  getIndicateurs: async (): Promise<IndicateursFinanciers> => {
    const res = await apiClient.get('/finance/indicateurs');
    return unwrap<IndicateursFinanciers>(res);
  },
};

/**
 * LOT A — les indicateurs financiers de la filiale, calculés côté serveur.
 * Les montants sont des chaînes de CENTIMES : un montant en FCFA dépasse
 * vite la précision d'un `number` JavaScript, et une créance ne s'arrondit
 * pas. Convertir à l'affichage seulement.
 */
export interface IndicateursFinanciers {
  filialeId:                string;
  arreteAu:                 string;
  encoursClientsCents:      string;
  encoursBrouillonsCents:   string;
  caMoisCents:              string;
  encaissementsMoisCents:   string;
  /** CA comptable (classe 7) − CA facturé. Non nul = anomalie à instruire. */
  ecartCaComptableCents:    string;
  balanceAgee: {
    tranches: Record<'a_jour' | 'j_0_30' | 'j_30_60' | 'j_60_90' | 'plus_90',
      { nbFactures: number; encoursCents: string }>;
    totalCents: string;
    nbFactures: number;
    /** Part échue de l'encours, déjà totalisée côté serveur. */
    enRetard:   { nbFactures: number; encoursCents: string };
  };
  tresorerieCents:          string;
  encoursFournisseursCents: string;
}

export interface BalanceAgeeBucket { count: number; encoursCents: string }
export interface BalanceAgeeClient {
  contactId:  string;
  nom:        string;
  nbFactures: number;
  total:      string;
  a_jour:     string;
  j_0_30:     string;
  j_30_60:    string;
  j_60_90:    string;
  plus_90:    string;
}
export interface BalanceAgeeFacture {
  id:           string;
  reference:    string;
  dueDate:      string;
  encoursCents: string;
  daysOverdue:  number;
  bucket:       'a_jour' | 'j_0_30' | 'j_30_60' | 'j_60_90' | 'plus_90';
  contact:      { id: string; fullName: string; companyName: string | null };
}
export interface BalanceAgee {
  asOf:    string;
  buckets: Record<'a_jour' | 'j_0_30' | 'j_30_60' | 'j_60_90' | 'plus_90', BalanceAgeeBucket>;
  total:   { nbFactures: number; encoursCents: string };
  parClient: BalanceAgeeClient[];
  factures:  BalanceAgeeFacture[];
}

export interface InvoiceQuery {
  invoiceType?: InvoiceType;
  status?:      InvoiceStatus;
  contactId?:   string;
  dateFrom?:    string;
  dateTo?:      string;
  page?:        number;
  limit?:       number;
}

export interface InvoiceLinePayload {
  produitId?:        string;
  designation:       string;
  quantite:          number;
  prixUnitaireCents: number;
}

export interface CreateInvoicePayload {
  invoiceType:  InvoiceType;
  reference?:   string;   // auto-générée PRO-YYYY-NNN si absente
  contactId:    string;
  issueDate:    string;
  dueDate:      string;
  lines:        InvoiceLinePayload[];
  taxCents?:    number;
  currency?:    string;
  /** Taux de remise % appliqué uniquement à partir de v2 (édition proforma) */
  remisePct?:   number;
  /** Conditions de paiement libres affichées sur la proforma PDF */
  paymentTerms?: string;
}

// ── Recouvrement ─────────────────────────────────────────────

/** especes débite 571000 (Caisse) ; tous les autres modes 521000 (Banque). */
export type ModeReglement = 'especes' | 'virement' | 'cheque' | 'mobile_money';

export interface CreateReglementPayload {
  /** Montant encaissé en centimes FCFA (multiple de 100). */
  montantCents:   number;
  mode:           ModeReglement;
  /** Date d'encaissement au format YYYY-MM-DD (défaut backend : aujourd'hui). */
  dateReglement?: string;
  /** N° de chèque, référence de virement, identifiant Wave / Orange Money… */
  reference?:     string;
  notes?:         string;
}

export interface Reglement {
  id:             string;
  invoiceId:      string;
  montantCents:   string;
  mode:           ModeReglement;
  dateReglement:  string;
  reference:      string | null;
  notes:          string | null;
  saisiPar?:      { id: string; fullName: string } | null;
  journalEntry?:  { id: string; reference: string } | null;
  createdAt:      string;
}

export interface ReglementResult {
  reglement:    Reglement;
  invoice:      Invoice;
  /** Ce qui reste dû APRÈS ce règlement, en centimes. */
  restantCents: number;
  /** true si la facture est soldée par ce règlement. */
  solde:        boolean;
}

/** Exercice comptable et ses mois (lot 2, étape 2.3). */
export interface PeriodeComptable {
  id:               string;
  numero:           number;
  dateDebut:        string;
  dateFin:          string;
  statut:           'ouverte' | 'cloturee';
  clotureeLe:       string | null;
  motifReouverture: string | null;
  cloturePar?:      { fullName: string } | null;
  _count?:          { ecritures: number };
}
export interface ExerciceComptable {
  id:       string;
  annee:    number;
  statut:   'ouvert' | 'clos';
  periodes: PeriodeComptable[];
}

/** Lignes d'un compte de tiers, pour le lettrage (lot 2, étape 2.4). Montants BigInt sérialisés en texte. */
export interface LigneLettrage {
  id:          string;
  debitCents:  string;
  creditCents: string;
  label:       string;
  contactId:   string | null;
  entry:       { id: string; numeroPiece: string | null; reference: string; entryDate: string; description: string };
  contact:     { id: string; fullName: string; companyName: string | null } | null;
  lettrage:    { lettrage: { id: string; code: string; automatique: boolean; annuleLe: string | null } } | null;
}
export interface LignesLettrage {
  lignes:    LigneLettrage[];
  nonLettre: { contactId: string; nom: string; debit: string; credit: string; solde: string }[];
}

/** Facture fournisseur (lot 2, étape 2.5). Montants BigInt sérialisés en texte. */
export interface FactureFournisseur {
  id:                string;
  reference:         string;
  numeroFournisseur: string;
  issueDate:         string;
  dueDate:           string;
  totalCents:        string;
  paidAmountCents:   string;
  status:            'draft' | 'partial' | 'paid' | 'cancelled';
  contact:           { id: string; fullName: string; companyName: string | null };
  reception:         { id: string; reference: string } | null;
  journalEntry:      { id: string; numeroPiece: string | null } | null;
  paiementsFournisseur: { id: string; montantCents: string; mode: string; datePaiement: string; journalEntry: { numeroPiece: string | null } }[];
}
export interface ReceptionAFacturer {
  id: string; reference: string; date: string; bonCommande: string; fournisseur: string; ttcCents: string;
}

/** États comptables (lot 2, étape 2.8) — montants en centimes, sérialisés en texte. */
export interface EtatBalance {
  du: string; au: string; equilibree: boolean; brouillonsExclus: number;
  comptes: { code: string; libelle: string; classe: string; ouvertureDebit: string; ouvertureCredit: string;
             mouvementDebit: string; mouvementCredit: string; soldeDebit: string; soldeCredit: string }[];
  classes: { classe: string; mouvementDebit: string; mouvementCredit: string; soldeDebit: string; soldeCredit: string }[];
  totaux: { ouvertureDebit: string; ouvertureCredit: string; mouvementDebit: string; mouvementCredit: string; soldeDebit: string; soldeCredit: string };
}
export interface EtatGrandLivre {
  du: string; au: string; brouillonsExclus: number;
  comptes: { code: string; libelle: string; ouverture: string; debit: string; credit: string; solde: string;
             lignes: { date: string; numeroPiece: string | null; reference: string; journal: string | null; libelle: string;
                       tiers: string | null; lettrage: string | null; debit: string; credit: string; solde: string }[] }[];
}
export interface EtatBalanceAgee {
  au: string; type: 'clients' | 'fournisseurs'; total: string;
  tranches: Record<'a_jour' | 'j_0_30' | 'j_30_60' | 'j_60_90' | 'plus_90', string>;
  parTiers: ({ contactId: string; nom: string; nb: number; total: string } & Record<'a_jour' | 'j_0_30' | 'j_30_60' | 'j_60_90' | 'plus_90', string>)[];
}
